// 刀 0 原样拷贝：拷贝自 Loop 单页（来源区间见 components/plan-lab/COPIED_FROM.json），之后独立演进。
// 只改接口前缀、缓存键、路由与 testid 前缀、import 路径；D-28 连带删除见登记表「本地改动」栏。
// PR5：轨迹抽屉升级成按层 ＋ 按事项分组（plan-trace-drawer.tsx 等）；本文件留下对话栏、背景卡等共用件，
// 以及模拟场景页（PR9 前仍是原样拷贝）还在用的平铺三栏。手搭 Radix 抽屉删掉（改 ui/Drawer）、手写表格改 PlanTable、
// 11 号字改 text-xs、裸圆角改 rounded-md（豁免清单里轨迹抽屉那条随之删除）。
import { Badge } from "@/components/ui/badge";
import {
	type NodeEvent,
	type OutputBlock,
	type Scenario,
	type Timing,
	type TraceStep,
	type TurnView,
	planErrorMessage,
	useCatalog,
} from "@/lib/api/plan-lab";
import { type ReactNode, useEffect, useState } from "react";

import { LoadingInline } from "@/components/ui/feedback";
import { useDemandLabel } from "../kit/demand-label";
import { FailState } from "../kit/fail-state";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

export function JsonBox({ value }: { value: unknown }) {
	return (
		<pre className="overflow-x-auto rounded-md border border-surface-border bg-surface-muted/40 p-3 font-mono text-xs leading-5 text-text-primary">
			{JSON.stringify(value, null, 2)}
		</pre>
	);
}

export type Tone = "success" | "warning" | "danger" | "info" | "muted";
export const TONE: Record<Tone, string> = {
	success: "bg-brand-success/10 text-brand-success",
	warning: "bg-brand-warning/10 text-brand-warning",
	danger: "bg-brand-danger/10 text-brand-danger",
	info: "bg-brand-info/10 text-brand-info", // C25 批3:info 真色(原借用近黑=灰)
	muted: "bg-surface-muted text-text-secondary",
};
// C25 批2:Pill 收编为全站 Badge 原语(rounded-full;info 修为真 info 色;tone 单枚举)
export const Pill = Badge;

export function observeTone(observe: string): Tone {
	if (observe.startsWith("blocked")) return "warning";
	if (["render", "done"].includes(observe)) return "success";
	if (["cannot", "handoff"].includes(observe)) return "danger";
	if (observe === "confirm_gate" || observe === "multi") return "info";
	if (observe === "empty") return "muted";
	return "muted";
}
export function decideTone(decide: string): Tone {
	if (decide === "call") return "success";
	if (decide === "call_attempt") return "warning";
	if (decide === "chain") return "info";
	return "muted";
}
export const DECIDE_LABEL: Record<string, string> = {
	call: "调用",
	call_attempt: "试探",
	chain: "链式前置",
};

/** 中间过程:逐步铺开 */
export function TraceSteps({ trace }: { trace?: TraceStep[] }) {
	const [openIdx, setOpenIdx] = useState<number | null>(null);
	const steps = trace ?? [];
	if (!steps.length) {
		return <div className="text-xs text-text-muted">(本轮无工具调用)</div>;
	}
	return (
		<ol className="space-y-1.5">
			{steps.map((s, i) => (
				<li
					key={`${s.skill}-${i}`}
					className="rounded-md border border-surface-border bg-surface-card"
				>
					<button
						type="button"
						onClick={() => setOpenIdx(openIdx === i ? null : i)}
						className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
					>
						<span className="font-mono text-xs text-text-muted">{i + 1}</span>
						<Pill tone={decideTone(s.decide)}>{DECIDE_LABEL[s.decide] ?? s.decide}</Pill>
						<span className="font-mono text-text-primary">{s.skill}</span>
						<span className="text-text-muted">→</span>
						<Pill tone={observeTone(s.observe)}>{s.observe}</Pill>
						{s.reason ? (
							<span className="truncate text-xs text-text-muted">· {s.reason}</span>
						) : null}
						<span className="ml-auto shrink-0 text-xs text-text-muted">
							{openIdx === i ? "收起入参" : "看入参"}
						</span>
					</button>
					{openIdx === i ? (
						<div className="space-y-2 px-3 pb-3">
							{s.reason ? (
								<div className="text-xs text-text-secondary">主 Agent 决策理由:{s.reason}</div>
							) : null}
							<JsonBox value={s.params ?? {}} />
						</div>
					) : null}
				</li>
			))}
		</ol>
	);
}

/* ====================== 统一执行还原组件:ExecutionTrace ======================
   节点级逐步还原一轮真实执行:N2每轮决策/N3生成(LLM·含渲染后提示词+原始响应)
   + B2.5组装/G1必填/G6排除/G4判停(规则)+ 工具(完整 I/O)。全站(场景库/试跑/历史)复用此一处。
   无 node_trace 的旧记录 → 兜底回退到简版 TraceSteps。 */
export const NODE_LABEL: Record<string, { label: string; tone: Tone }> = {
	n2_decide: { label: "N2 决策", tone: "info" },
	n3_generate: { label: "N3 生成", tone: "info" },
};
export function nodeBadge(e: NodeEvent): { label: string; tone: Tone } {
	if (e.kind === "llm") return NODE_LABEL[e.node] ?? { label: e.node, tone: "info" };
	if (e.kind === "tool") return { label: e.node ? `工具 ${e.node}` : "工具", tone: "success" }; // PR3：标出工具名
	if (e.kind === "rule") {
		if (e.node === "O2") return { label: "O2 指南召回", tone: "info" };
		return { label: `规则 ${e.node}`, tone: "muted" }; // 常规过程降噪:warning 只留异常
	}
	return { label: "说明", tone: "muted" };
}

// 业务中文名映射(全站统一,让标签一眼业务可读);诉求 ／ 意图显示名只读意图表(kit/demand-label)
export const SKILL_ZH: Record<string, string> = {
	"search-order": "查订单",
	"shipping-status": "查物流",
	"product-info": "单品问询",
	"product-recommend": "推荐",
	"product-compare": "对比",
	"find-similar": "找相似",
	"discount-code": "优惠码",
	"knowledge-search": "知识检索",
	refund: "退款",
	return: "退货",
	"cancel-order": "取消订单",
	"update-address": "改地址",
	handoff: "转人工",
};
export const ACTION_ZH: Record<string, string> = {
	call_skill: "调用工具",
	clarify: "反问澄清",
	done: "收尾",
};
// N3 应答类型(互斥 7 类)中文名
export const REPLY_TYPE_ZH: Record<string, string> = {
	inform: "说明(有依据)",
	clarify: "澄清",
	confirm: "确认",
	followup: "追问承接",
	chitchat: "闲聊",
	decline: "越界婉拒",
	handoff: "转人工",
};
export function skillZh(s: string): string {
	return SKILL_ZH[s] ? `${s}(${SKILL_ZH[s]})` : s;
}

export function IOBlock({ label, value }: { label: string; value: unknown }) {
	if (value === undefined || value === null || value === "") return null;
	return (
		<div>
			<div className="mb-1 text-xs font-medium text-text-secondary">{label}</div>
			{typeof value === "string" ? (
				<pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-surface-border bg-surface-muted/40 p-3 font-mono text-xs leading-5 text-text-primary">
					{value}
				</pre>
			) : (
				<JsonBox value={value} />
			)}
		</div>
	);
}

/* ---- 核心输出标签:全部由真实 output 结构派生(适配各情况,不写死字段)---- */
// 把任意对象压成一眼可读的紧凑标签:list→key:N,bool→key,标量→key:value,嵌套→key:{子键}
export function compactTags(obj: unknown, max = 5): string[] {
	if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
	const out: string[] = [];
	for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
		if (v === null || v === undefined || v === "") continue;
		if (Array.isArray(v)) out.push(`${k}:${v.length}`);
		else if (typeof v === "object") {
			const ks = Object.keys(v as object);
			if (ks.length) out.push(`${k}:{${ks.slice(0, 3).join(",")}}`);
		} else if (typeof v === "boolean") {
			if (v) out.push(k);
		} else out.push(`${k}:${String(v).slice(0, 28)}`);
		if (out.length >= max) break;
	}
	return out;
}

export type NodeTagView = {
	primary?: { label: string; tone: Tone };
	tags: string[];
	note?: string;
};
// 每个节点的"核心产出"标签 + 业务说明(note):从真实 output 取关键值,缺字段自然省略
export function nodeTagView(e: NodeEvent): NodeTagView {
	const out = e.output ?? {};
	if (e.kind === "tool") {
		const payload = (out.payload ?? {}) as Record<string, unknown>;
		const { status, _backend, _recorded_miss, _live_fallback, ...rest } = payload as {
			status?: unknown;
			_backend?: unknown;
			_recorded_miss?: unknown;
			_live_fallback?: unknown;
		};
		const s = status ? String(status) : "";
		const tags = compactTags(rest);
		// 调用来源(真实执行/录制回放/罐头模拟)——运营一眼分清这次是真数据还是模拟
		if (_backend) {
			const b = String(_backend);
			tags.unshift(
				b === "live" ? "来源:真实执行" : b === "recorded" ? "来源:录制回放" : "来源:罐头模拟",
			);
			if (_recorded_miss) tags.push("存档未命中→已回落罐头");
			if (_live_fallback) tags.push(`真实执行回落:${String(_live_fallback)}`);
		}
		return {
			primary: s ? { label: s, tone: observeTone(s) } : undefined,
			tags,
		};
	}
	if (e.kind === "rule") {
		if (e.node === "O2") {
			// 应答指南召回(P2):命中集与截断一眼可见;采纳看 N3 生成事件的「采纳」标签
			const ids = (out.hit_ids as string[] | undefined) ?? [];
			const trimmed = Number(out.trimmed ?? 0);
			return {
				primary: {
					label: `召回 ${ids.length} 条${trimmed ? `(截断 ${trimmed})` : ""}`,
					tone: "info",
				},
				tags: ids,
			};
		}
		if (e.node === "G1") {
			const missing = (out.missing as unknown[]) ?? [];
			return missing.length
				? { primary: { label: `缺:${missing.join(",")}`, tone: "warning" }, tags: [] }
				: { primary: { label: "必填齐全", tone: "success" }, tags: [] };
		}
		if (e.node === "G4") {
			const stop = out.stop ? String(out.stop) : "";
			return {
				primary: stop ? { label: `stop:${stop}`, tone: observeTone(stop) } : undefined,
				tags: [],
			};
		}
		if (e.node === "G6")
			return { primary: { label: `排除→${out.swapped_to ?? "?"}`, tone: "warning" }, tags: [] };
		if (e.node === "B2.5") {
			const keys = Object.keys((out.params ?? {}) as object);
			const tags = keys.length ? [...keys] : ["(空参)"];
			// resolve=组参内部解析过程(C23 档3):选行模式/多目标取首/地址查表/枚举校验 可见
			const resolve = (out.resolve ?? {}) as {
				line_pick?: { mode?: string };
				order?: { multi_first_only?: boolean };
				addr?: { hit?: boolean };
				reason?: { enum_ok?: boolean };
			};
			if (resolve.line_pick?.mode) tags.unshift(`选行:${resolve.line_pick.mode}`);
			if (resolve.order?.multi_first_only) tags.push("多目标取首个(C24⏸)");
			if (resolve.addr) tags.push(resolve.addr.hit ? "地址簿命中" : "地址簿未命中");
			if (resolve.reason && !resolve.reason.enum_ok) tags.push("退货原因枚举不合法");
			return { tags };
		}
		return { tags: compactTags(out) };
	}
	if (e.kind === "llm") {
		const parsed = (out.parsed ?? null) as Record<string, unknown> | null;
		if (e.node === "n2_decide" && parsed) {
			const act = parsed.action ? String(parsed.action) : "";
			const tags: string[] = [];
			if (parsed.skill) tags.push(skillZh(String(parsed.skill)));
			if (parsed.slot) tags.push(`反问槽:${parsed.slot}`);
			return {
				primary: act ? { label: ACTION_ZH[act] ?? act, tone: "info" } : undefined,
				tags,
				note: parsed.reason ? `理由:${parsed.reason}` : undefined, // 大模型为何这么决策
			};
		}
		if (e.node === "n3_generate") {
			const blocks = (parsed?.blocks as Array<Record<string, unknown>> | undefined) ?? [];
			const texts = blocks
				.filter((b) => b.kind === "text")
				.map((b) => String(b.text ?? ""))
				.join(" ");
			const nProd = blocks.filter((b) => b.kind === "product").length;
			const nCoup = blocks.filter((b) => b.kind === "coupon").length;
			const t = parsed?.reply_type ? String(parsed.reply_type) : "";
			const tags: string[] = [];
			if (nProd) tags.push(`商品卡×${nProd}`);
			if (nCoup) tags.push(`券卡×${nCoup}`);
			const appliedG = (parsed?.applied_guidelines as string[] | undefined) ?? [];
			for (const gid of appliedG) tags.push(`采纳:${gid}`);
			return {
				primary: { label: t ? `应答:${REPLY_TYPE_ZH[t] ?? t}` : "已生成", tone: "success" },
				tags,
				note: texts || (out.raw ? String(out.raw) : undefined),
			};
		}
		return { tags: parsed ? compactTags(parsed) : [] };
	}
	return { tags: compactTags(e.input ?? {}) }; // note
}

/* ---- 节点完整 I/O 详情(③ 抽屉右栏复用)---- */
export function NodeDetail({ e }: { e: NodeEvent }) {
	const inp = e.input ?? {};
	const out = e.output ?? {};
	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
				<Pill tone={nodeBadge(e).tone}>{nodeBadge(e).label}</Pill>
				<span>{e.title}</span>
				<Pill tone={msTone(e.ms)}>耗时 {fmtMs(e.ms)}</Pill>
				{e.usage ? (
					<Pill tone="success">
						token {e.usage.total_tokens.toLocaleString("en-US")}(入 {e.usage.prompt_tokens} · 出{" "}
						{e.usage.completion_tokens})
					</Pill>
				) : null}
			</div>
			{e.kind === "llm" ? (
				<>
					<IOBlock label="① system(固定指令)" value={inp.prompt} />
					<IOBlock label="② user(输入变量 + 当前句,真实送给模型)" value={inp.trigger} />
					<IOBlock label="③ 本轮输入的变量值" value={inp.vars} />
					<IOBlock label="④ 模型原始响应" value={out.raw} />
					{out.parsed ? <IOBlock label="⑤ 解析结果" value={out.parsed} /> : null}
				</>
			) : e.kind === "tool" ? (
				<>
					{(() => {
						const b = String(((out.payload ?? {}) as Record<string, unknown>)._backend ?? "");
						if (!b) return null;
						return (
							<div className="flex items-center gap-2 text-xs">
								<span className="text-text-muted">调用来源</span>
								<Badge tone={b === "live" ? "success" : b === "recorded" ? "info" : "muted"}>
									{b === "live" ? "真实执行" : b === "recorded" ? "录制回放" : "罐头模拟"}
								</Badge>
							</div>
						);
					})()}
					<IOBlock label="入参 params" value={inp.params} />
					<IOBlock label="完整返回 payload" value={out.payload} />
				</>
			) : (
				<>
					<IOBlock label="输入" value={inp} />
					<IOBlock label="输出" value={out} />
				</>
			)}
		</div>
	);
}

/* ---- ② 环节过程:一行节点 = 徽章 + 标题 + 核心输出标签;点击选中看 ③ ---- */
export function NodeStepRow({
	e,
	selected,
	onSelect,
}: { e: NodeEvent; selected: boolean; onSelect: () => void }) {
	const b = nodeBadge(e);
	const tv = nodeTagView(e);
	return (
		<button
			type="button"
			onClick={onSelect}
			className={`w-full rounded-lg border px-2.5 py-2 text-left text-sm transition-colors duration-150 ${
				selected
					? "border-brand-accent bg-brand-accent/5"
					: "border-surface-border bg-surface-card hover:bg-surface-subtle"
			}`}
		>
			<div className="flex flex-wrap items-center gap-1.5">
				<span className="w-4 shrink-0 font-mono text-xs text-text-muted">{e.seq}</span>
				<Pill tone={b.tone}>{b.label}</Pill>
				{tv.primary ? <Pill tone={tv.primary.tone}>{tv.primary.label}</Pill> : null}
				{tv.tags.map((t) => (
					<span
						key={t}
						className="rounded-full bg-surface-muted px-1.5 py-0.5 font-mono text-xs text-text-secondary"
					>
						{t}
					</span>
				))}
				<span className="ml-auto flex shrink-0 items-center gap-1.5">
					<Pill tone={msTone(e.ms)}>{fmtMs(e.ms)}</Pill>
					<span className="text-xs text-text-muted">{selected ? "▶" : ""}</span>
				</span>
			</div>
			{tv.note ? (
				<div className="mt-1 line-clamp-2 pl-6 text-xs text-text-muted">{tv.note}</div>
			) : null}
		</button>
	);
}

/* ====================== 统一执行还原抽屉:ExecutionDrawer ======================
   右侧三栏:① 聊天记录(全轮气泡,user 右/主 Agent 左) | ② 环节过程(节点+核心标签,点选)
   | ③ 详情(点环节才出,完整 I/O)。场景库/历史/试跑 三页共用此一处。 */
export type DrawerTurn = {
	msg: string;
	resume?: string;
	reply?: string;
	stop?: string | null;
	intent?: string | null;
	committed?: boolean;
	calls?: string[];
	terminal?: string | null;
	clarify?: string | null;
	node_trace?: NodeEvent[];
	trace?: TraceStep[];
	timing?: Timing;
	blocks?: OutputBlock[];
	expect?: Record<string, unknown>; // 该轮期望断言(场景脚本)
	pending?: boolean; // 试跑:本轮已发出、正在调模型(乐观 UI + loading)
};

/** 结构化输出渲染:逐块(商品卡/优惠券卡/图片;text 块由气泡正文展示,这里跳过)*/
export function ReplyBlocks({ blocks }: { blocks?: OutputBlock[] }) {
	const cards = (blocks ?? []).filter((b) => b.kind !== "text");
	if (!cards.length) return null;
	return (
		<div className="mt-1.5 flex flex-wrap gap-2">
			{cards.map((c, i) => {
				if (c.kind === "product")
					return (
						<div
							key={`p-${c.productId}`}
							className="flex w-44 items-center gap-2 rounded-lg border border-surface-border bg-surface-card p-2"
						>
							<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-surface-muted text-lg">
								🛍️
							</div>
							<div className="min-w-0">
								<div className="truncate text-sm font-medium text-text-primary" title={c.title}>
									{c.title}
								</div>
								<div className="text-xs text-text-muted">{c.productId}</div>
								<div className="text-sm font-semibold text-brand-danger">
									{c.price_text ?? c.price}
								</div>
							</div>
						</div>
					);
				if (c.kind === "coupon")
					return (
						<div
							key={`c-${c.code}`}
							className="flex w-44 items-center gap-2 rounded-lg border border-dashed border-brand-danger/40 bg-brand-danger/5 p-2"
						>
							<div className="whitespace-nowrap text-xl font-bold text-brand-danger">
								{c.type === "percent" ? `${c.amount}%off` : (c.amount_text ?? c.amount)}
							</div>
							<div className="min-w-0">
								<div className="font-mono text-sm font-medium text-text-primary">{c.code}</div>
								<div className="truncate text-xs text-text-muted" title={c.desc}>
									{c.desc}
								</div>
							</div>
						</div>
					);
				// image(预留)
				return (
					// biome-ignore lint/suspicious/noArrayIndexKey: image 块无稳定 id
					<img key={`img-${i}`} src={c.url} alt="" className="h-20 w-20 rounded-md object-cover" />
				);
			})}
		</div>
	);
}

/** ③ 详情可看的对象:节点 / 背景客户 / 预期vs实际 */
export type DrawerSel = { kind: "node"; seq: number } | { kind: "bg" } | { kind: "expect" } | null;

export function fmtMs(ms: number): string {
	return ms >= 1000
		? `${(ms / 1000).toFixed(2)}s`
		: `${ms < 10 ? ms.toFixed(1) : Math.round(ms)}ms`;
}
export function msTone(ms: number): Tone {
	return ms >= 1500 ? "danger" : ms >= 500 ? "warning" : "muted";
}

/** 详情抽屉统一面板眉:加粗标题 + 分隔线(三栏 / 评分卡各区共用,立层级、去数字)。 */
export function PanelHead({
	title,
	hint,
	extra,
}: {
	title: string;
	hint?: ReactNode;
	extra?: ReactNode;
}) {
	return (
		<div className="flex flex-wrap items-center gap-1.5 border-b border-surface-border pb-1.5">
			<span className="text-xs font-bold text-text-primary">{title}</span>
			{hint ? <span className="text-xs text-text-muted">{hint}</span> : null}
			{extra}
		</div>
	);
}

export function ChatColumn({
	turns,
	focus,
	onFocus,
	footer,
	emptyHint,
	topExtra,
}: {
	turns: DrawerTurn[];
	focus: number;
	onFocus: (i: number) => void;
	footer?: ReactNode; // 试跑模式:底部输入框(只读抽屉不传)
	emptyHint?: ReactNode;
	topExtra?: ReactNode; // PR5:对话栏顶「背景」信息块(线框 01 §三)
}) {
	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="plan-col-chat">
			<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
				<PanelHead title="对话" hint="🧑 用户 · 🤖 客服 · 点轮次看执行" />
				{topExtra ? <div className="flex flex-wrap items-center gap-2">{topExtra}</div> : null}
				{!turns.length && emptyHint ? (
					<div className="text-sm text-text-muted">{emptyHint}</div>
				) : null}
				{turns.map((t, i) => (
					<button
						key={`${t.msg}-${i}`}
						type="button"
						onClick={() => onFocus(i)}
						className={`space-y-2 rounded-lg border p-2.5 text-left transition-colors duration-150 ${
							i === focus
								? "border-brand-accent bg-brand-accent/5"
								: "border-surface-border hover:bg-surface-subtle/60"
						}`}
					>
						<div className="flex items-center gap-1.5">
							<span className="font-mono text-xs text-text-muted">第{i + 1}轮</span>
							{t.pending ? <Pill tone="info">运行中…</Pill> : null}
							{t.resume ? <Pill tone="muted">{RESUME_LABEL[t.resume] ?? t.resume}</Pill> : null}
							{t.stop ? (
								<Pill tone={t.stop === "aborted" ? "danger" : observeTone(t.stop)}>{t.stop}</Pill>
							) : null}
							{t.committed ? <Pill tone="success">已执行</Pill> : null}
						</div>
						{/* 用户=浅灰圆角块(Intercom P9);客服=无气泡文档流(拍板④) */}
						<div className="flex justify-start gap-1.5">
							<span className="mt-0.5 shrink-0 text-sm" aria-hidden>
								🧑
							</span>
							<div className="max-w-[85%] rounded-lg rounded-tl-sm bg-surface-muted px-2.5 py-1.5 text-sm text-text-primary">
								{t.msg}
							</div>
						</div>
						{t.pending ? (
							<div className="flex items-center gap-1.5 pl-6 text-sm text-text-secondary">
								<span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-accent" />
								正在调模型…
							</div>
						) : t.reply ? (
							<div className="space-y-1 pl-6">
								<div className="text-xs text-text-muted">🤖 客服</div>
								<div className="whitespace-pre-wrap text-sm leading-6 text-text-primary">
									{t.reply}
								</div>
								<ReplyBlocks blocks={t.blocks} />
							</div>
						) : (
							<div className="pl-6 text-xs text-text-muted">
								(本轮无话术 · 离线机制验证 / 未开 N3)
							</div>
						)}
					</button>
				))}
			</div>
			{footer ? (
				<div className="border-t border-surface-border bg-surface-card p-2">{footer}</div>
			) : null}
		</div>
	);
}

/** 信息块 chip(背景/预期),点击 → ③ 看详情 */
export function InfoChip({
	label,
	tone,
	active,
	onClick,
}: { label: string; tone: Tone; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-150 ${active ? "border-brand-accent bg-brand-accent/5 text-text-primary" : "border-surface-border text-text-secondary hover:bg-surface-subtle"}`}
		>
			<span
				className={`h-1.5 w-1.5 rounded-full ${tone === "info" ? "bg-brand-info" : "bg-text-muted"}`}
			/>
			{label}
		</button>
	);
}

export function StepsColumn({
	turn,
	selected,
	onSelect,
	customerName,
}: {
	turn?: DrawerTurn;
	selected: DrawerSel;
	onSelect: (s: DrawerSel) => void;
	customerName?: string;
}) {
	const demandLabel = useDemandLabel();
	const events = turn?.node_trace ?? [];
	const tm = turn?.timing;
	return (
		<div className="flex min-h-0 flex-col gap-2 overflow-y-auto p-3" data-testid="plan-col-steps">
			<PanelHead
				title="环节"
				extra={turn?.intent ? <Pill tone="info">{demandLabel(turn.intent)}</Pill> : null}
			/>
			{turn?.pending ? (
				<div className="flex items-center gap-2 rounded-lg bg-surface-subtle px-2.5 py-2 text-xs text-text-secondary">
					<span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-accent" />
					环节执行中…(信号 → N2 决策 → 工具 → N3 应答,完成后逐环节展开)
				</div>
			) : null}
			{/* 信息块:背景 / 预期 → 点击右侧看详情 */}
			<div className="flex flex-wrap items-center gap-2">
				{customerName ? (
					<InfoChip
						label={`背景·客户 ${customerName}`}
						tone="muted"
						active={selected?.kind === "bg"}
						onClick={() => onSelect({ kind: "bg" })}
					/>
				) : null}
				{turn?.expect ? (
					<InfoChip
						label="预期结果"
						tone="info"
						active={selected?.kind === "expect"}
						onClick={() => onSelect({ kind: "expect" })}
					/>
				) : null}
			</div>
			{tm?.total_ms !== undefined ? (
				<div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-surface-muted/40 px-2.5 py-1.5 text-xs text-text-secondary">
					<span className="font-medium text-text-primary">本轮耗时</span>
					<Pill tone={msTone(tm.total_ms)}>总 {fmtMs(tm.total_ms)}</Pill>
					<span>LLM {fmtMs(tm.llm_ms ?? 0)}</span>
					<span>· 工具 {fmtMs(tm.tool_ms ?? 0)}</span>
					<span>· 规则 {fmtMs(tm.rule_ms ?? 0)}</span>
					<span>· LLM 调用 {tm.llm_calls ?? 0} 次</span>
				</div>
			) : null}
			{tm?.tokens_total !== undefined ? (
				<div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-surface-muted/40 px-2.5 py-1.5 text-xs text-text-secondary">
					<span className="font-medium text-text-primary">本轮 token</span>
					<Pill tone="success">合计 {(tm.tokens_total ?? 0).toLocaleString("en-US")}</Pill>
					<span>输入 {(tm.tokens_in ?? 0).toLocaleString("en-US")}</span>
					<span>· 输出 {(tm.tokens_out ?? 0).toLocaleString("en-US")}</span>
				</div>
			) : null}
			{events.length ? (
				events.map((e) => (
					<NodeStepRow
						key={e.seq}
						e={e}
						selected={selected?.kind === "node" && selected.seq === e.seq}
						onSelect={() => onSelect({ kind: "node", seq: e.seq })}
					/>
				))
			) : turn?.trace?.length ? (
				<TraceSteps trace={turn.trace} /> // 旧记录兜底
			) : (
				<div className="text-xs text-text-muted">该轮无执行轨迹(未跑或越界直收)。</div>
			)}
		</div>
	);
}

type SeedOrderRow = { orderId: string; fulfillment_status: string | null; items: string };
const ORDER_COLS: PlanColumn<SeedOrderRow>[] = [
	{
		key: "id",
		title: "订单",
		className: "font-mono text-text-primary",
		render: (o) => o.orderId.replace("gid://shopify/Order/", "#"),
	},
	{
		key: "st",
		title: "发货",
		render: (o) => (
			<Pill tone={observeTone(o.fulfillment_status ?? "")}>{o.fulfillment_status}</Pill>
		),
	},
	{ key: "items", title: "商品", className: "text-text-secondary", render: (o) => o.items },
];

/** 客户背景画像(姓名/背景/邮箱/订单)*/
export function CustomerCard({ customerId }: { customerId?: string }) {
	const cat = useCatalog();
	const seed = cat.data?.seed;
	const c = seed?.customers.find((x) => x.customer_id === customerId);
	const orders = (seed?.orders ?? []).filter((o) => o.customer_id === customerId);
	if (cat.isLoading) return <LoadingInline />;
	if (cat.isError)
		return (
			<FailState
				title="客户背景没加载出来"
				reason={planErrorMessage(cat.error)}
				onRetry={() => cat.refetch()}
				retrying={cat.isFetching}
			/>
		);
	if (!c)
		return (
			<div className="text-sm text-text-muted">无客户背景(customer_id={customerId ?? "—"})。</div>
		);
	return (
		<div className="space-y-3">
			<div className="rounded-md border border-surface-border p-3 text-sm">
				<div className="flex flex-wrap items-center gap-2">
					<Pill tone="info">{c.customer_id}</Pill>
					<span className="font-medium text-text-primary">{c.name}</span>
					<span className="text-text-muted">{c.email}</span>
				</div>
				<div className="mt-1 text-text-secondary">背景:{c.bg}</div>
				<div className="text-text-muted">名下订单 {c.order_count} 单</div>
			</div>
			<div>
				<div className="mb-1 text-xs font-medium text-text-secondary">该客户订单</div>
				{orders.length ? (
					<PlanTable
						columns={ORDER_COLS}
						rows={orders}
						rowKey={(o) => o.orderId}
						density="compact"
					/>
				) : (
					<div className="text-xs text-text-muted">该客户无订单(新客)。</div>
				)}
			</div>
		</div>
	);
}

type ExpLine = { label: string; exp: unknown; act: unknown };
const EXP_COLS: PlanColumn<ExpLine>[] = [
	{ key: "l", title: "维度", className: "text-text-secondary", render: (r) => r.label },
	{
		key: "e",
		title: "预期",
		className: "font-mono text-text-primary",
		render: (r) => JSON.stringify(r.exp),
	},
	{
		key: "a",
		title: "实际",
		className: "font-mono text-text-secondary",
		render: (r) => JSON.stringify(r.act ?? null),
	},
	{
		key: "ok",
		title: "符",
		render: (r) =>
			JSON.stringify(r.exp) === JSON.stringify(r.act ?? null) ? (
				<Pill tone="success">✓</Pill>
			) : (
				<Pill tone="danger">✗</Pill>
			),
	},
];

/** 预期 vs 实际(golden_skills / stop / clarify / terminal / committed)*/
export function ExpectedVsActual({ turn }: { turn?: DrawerTurn }) {
	const exp = turn?.expect ?? {};
	const goldenCalls = (exp.calls as string[] | undefined) ?? undefined;
	const lines: ExpLine[] = [
		{ label: "判停 stop", exp: exp.stop, act: turn?.stop },
		{ label: "反问 clarify", exp: exp.clarify, act: turn?.clarify },
		{ label: "收尾 terminal", exp: exp.terminal, act: turn?.terminal },
		{ label: "已执行 committed", exp: exp.committed, act: turn?.committed },
		{ label: "调用链 calls", exp: goldenCalls, act: turn?.calls },
		{ label: "轮数 rounds", exp: exp.rounds, act: turn?.trace?.length },
	].filter((l) => l.exp !== undefined);
	return (
		<div className="space-y-3">
			<PlanTable columns={EXP_COLS} rows={lines} rowKey={(l) => l.label} density="compact" />
			<div className="text-xs text-text-muted">
				预期来自场景脚本(golden);真 LLM 输出会波动,calls 顺序仅参考,机制看 stop/terminal。
			</div>
		</div>
	);
}

export function DetailColumn({
	selected,
	turn,
	customerId,
}: { selected: DrawerSel; turn?: DrawerTurn; customerId?: string }) {
	const node =
		selected?.kind === "node" ? turn?.node_trace?.find((e) => e.seq === selected.seq) : null;
	let title = "详情";
	let body: ReactNode;
	if (selected?.kind === "bg") {
		title = "背景 · 客户画像";
		body = <CustomerCard customerId={customerId} />;
	} else if (selected?.kind === "expect") {
		title = "预期 vs 实际";
		body = <ExpectedVsActual turn={turn} />;
	} else if (node) {
		title = "节点完整 I/O";
		body = <NodeDetail e={node} />;
	} else {
		body = (
			<div className="rounded-md border border-dashed border-surface-border p-6 text-center text-sm text-text-muted">
				点击中间的「环节」或顶部「背景 / 预期」信息块,这里显示对应详情。
			</div>
		);
	}
	return (
		<div className="min-h-0 space-y-2 overflow-y-auto p-3" data-testid="plan-col-detail">
			<PanelHead title={title} />
			{body}
		</div>
	);
}

/** 三栏执行视图(① 聊天 ② 环节 ③ 详情)—— 抽屉(只读)与试跑(带输入 footer)共用一套 */
export function ExecutionColumns({
	turns,
	customerId,
	chatFooter,
	chatEmptyHint,
	initialTurn = 0,
	resetKey = 0,
	focusLast = false,
	className,
}: {
	turns: DrawerTurn[];
	customerId?: string;
	chatFooter?: ReactNode; // 试跑:底部输入框
	chatEmptyHint?: ReactNode;
	initialTurn?: number;
	resetKey?: number; // 变化即重置选中轮(抽屉用 open 切换)
	focusLast?: boolean; // 试跑:新轮到达自动聚焦最新
	className?: string;
}) {
	const cat = useCatalog();
	// 客户列表没加载出来只少「背景」信息块里的客户名，不挡执行视图
	const customerName = cat.isError
		? undefined
		: cat.data?.seed.customers.find((c) => c.customer_id === customerId)?.name;
	const [turnIdx, setTurnIdx] = useState(initialTurn);
	const [sel, setSel] = useState<DrawerSel>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 仅在 resetKey/initialTurn 变化时重置选中
	useEffect(() => {
		setTurnIdx(initialTurn);
		setSel(null);
	}, [initialTurn, resetKey]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 新轮到达时聚焦最新一轮
	useEffect(() => {
		if (focusLast && turns.length) {
			setTurnIdx(turns.length - 1);
			setSel(null);
		}
	}, [turns.length]);
	const idx = Math.min(turnIdx, Math.max(0, turns.length - 1));
	const turn = turns[idx];
	return (
		<div
			className={`grid min-h-0 grid-rows-1 grid-cols-[300px_minmax(0,1.1fr)_minmax(0,1fr)] divide-x divide-surface-border overflow-hidden ${className ?? ""}`}
		>
			<ChatColumn
				turns={turns}
				focus={idx}
				onFocus={(i) => {
					setTurnIdx(i);
					setSel(null);
				}}
				footer={chatFooter}
				emptyHint={chatEmptyHint}
			/>
			<StepsColumn turn={turn} selected={sel} onSelect={setSel} customerName={customerName} />
			<DetailColumn selected={sel} turn={turn} customerId={customerId} />
		</div>
	);
}

export function turnViewsToDrawer(turns: TurnView[], spec?: Scenario["turns"]): DrawerTurn[] {
	return turns.map((t, i) => ({
		msg: t.msg,
		resume: t.resume,
		reply: t.reply,
		stop: t.stop,
		intent: t.intent,
		committed: t.committed,
		calls: t.calls,
		terminal: t.terminal,
		clarify: t.clarify,
		node_trace: t.node_trace,
		trace: t.trace,
		timing: t.timing,
		blocks: t.blocks,
		expect: spec?.[i]?.expect,
	}));
}

export const RESUME_LABEL: Record<string, string> = {
	new: "新轮",
	provide: "补槽",
	confirm: "确认",
	cancel: "反悔",
	switch: "切换",
};
